/** Формирование короткого отображаемого имени с процентажем */
export function getDisplayName(name: string, percentage: number): string {
  if (percentage < 15) return `${percentage}%`;
  if (percentage < 25 && name.length > 8) return `${name.slice(0, 7)}… ${percentage}%`;
  return `${name} ${percentage}%`;
}

/** Получение URL изображения игрока */
export function getPlayerImageUrl(photo?: string): string | null {
  if (!photo) return null;
  const photoWithPng = photo.replace(/\.jpg$/i, '.png');
  return `https://static.crowdfpl.com/players/25/${photoWithPng}`;
}

/** Получение URL формы команды */
export function getTeamShirtUrl(team?: { code?: string } | string): string | null {
  const code = typeof team === 'string' ? team : team?.code;
  if (!code) return null;
  return `https://static.crowdfpl.com/shirts/standard/shirt_${code}-220.webp`;
}

/** Format date to "12 Feb" */
export function formatMatchDate(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  return `${day} ${month}`;
}

/** Format time to "14:30" (hours:minutes) */
export function formatMatchTime(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Format date to "Sat 13/12 15:30" */
export function formatMatchDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const dayOfWeek = date.toLocaleString('en-US', { weekday: 'short' });
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${dayOfWeek} ${day}/${month} ${hours}:${minutes}`;
}

/** Format date to "15:30 13/12 Sat" (reversed for right player) */
export function formatMatchDateTimeReversed(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const dayOfWeek = date.toLocaleString('en-US', { weekday: 'short' });
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes} ${day}/${month} ${dayOfWeek}`;
}

/** Форматирование значения рейтинга (potential rating) */
export function formatRatingValue(value: number): string {
  const formatted = Number.isInteger(value) ? value.toString() : value.toFixed(1);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return value.toFixed(1);
  return '0';
}

/** Format rating change for result footer (similar to VoteRow) */
export function formatRatingChange(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return '0';
  const formatted = Number.isInteger(delta) ? delta.toString() : delta.toFixed(1);
  if (delta > 0) return `+${formatted}`;
  if (delta < 0) return `−${Math.abs(delta).toFixed(1).replace(/\.0$/, '')}`;
  return '0';
}

/** Helper type for opponent info in DGW */
export interface OpponentInfo {
  opponentShortName: string;
  homeAway: 'H' | 'A';
  isLive: boolean;
  fixture: FixtureForOpponent;
}

interface FixtureForOpponent {
  kickoff_time?: string | null;
  team_h?: { id: string; short_name: string } | null;
  team_a?: { id: string; short_name: string } | null;
  team_h_score?: number | null;
  team_a_score?: number | null;
  finished?: boolean | null;
  finished_provisional?: boolean | null;
  started?: boolean | null;
  player_is_final?: boolean | null;
}

/** FPL can leave `finished=false` for hours after FT; treat provisional/final points as complete. */
export function isFixtureComplete(fixture: {
  finished?: boolean | null;
  finished_provisional?: boolean | null;
  player_is_final?: boolean | null;
}): boolean {
  return fixture.finished === true || fixture.finished_provisional === true || fixture.player_is_final === true;
}

export function isFixtureLive(fixture: {
  started?: boolean | null;
  finished?: boolean | null;
  finished_provisional?: boolean | null;
  player_is_final?: boolean | null;
}): boolean {
  return fixture.started === true && !isFixtureComplete(fixture);
}

/** Get ALL opponents info for DGW support */
export function getAllOpponents(player: {
  team: { id: string };
  gameweek_stats?: { fixtures?: FixtureForOpponent[] };
}): OpponentInfo[] {
  const gameweekStats = player.gameweek_stats;
  if (!gameweekStats?.fixtures || gameweekStats.fixtures.length === 0) return [];

  const playerTeamId = player.team.id;

  return gameweekStats.fixtures
    .map((fixture) => {
      const isHome = fixture.team_h?.id === playerTeamId;
      const opponent = isHome ? fixture.team_a : fixture.team_h;
      if (!opponent) return null;

      const now = Date.now();
      const kickoffTime = fixture.kickoff_time ? new Date(fixture.kickoff_time).getTime() : null;
      const isLive =
        !isFixtureComplete(fixture) &&
        (fixture.started === true || (kickoffTime !== null && kickoffTime <= now));

      return {
        opponentShortName: opponent.short_name,
        homeAway: (isHome ? 'H' : 'A') as 'H' | 'A',
        isLive,
        fixture,
      };
    })
    .filter((o): o is OpponentInfo => o !== null);
}
